import { Suspense, lazy } from 'react'
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router'
import { MotionConfig } from 'motion/react'
import { ErrorBoundary } from '../components/shared/error-boundary'
import { NotFoundPage } from '../components/shared/not-found'
import { Toaster } from 'sonner'

import { useSystemSettings } from '../stores/settings.store'
import { useThemeEffect } from '../hooks/use-theme-effect'
import { useDocumentTitle } from '../hooks/use-document-title'
import { DemoModeIndicator } from '../components/shared/demo-mode-indicator'

import type { QueryClient } from '@tanstack/react-query'
import type { Store } from '@tanstack/react-store'
import type { AuthState } from '../stores/auth.store'

const DevtoolsPanel = lazy(() => import('../integrations/devtools-panel'))

interface MyRouterContext {
  queryClient: QueryClient
  auth: Store<AuthState>
}

function RootComponent() {
  // Fetch system settings on app initialization
  // Settings are cached for 24 hours and used throughout the app
  useSystemSettings()

  // Apply theme class to document based on user preference
  useThemeEffect()

  // Update browser tab title based on current route
  useDocumentTitle()

  // Only show devtools when ?devtools=true is in the URL
  const showDevtools = new URLSearchParams(window.location.search).get('devtools') === 'true'

  return (
    <MotionConfig reducedMotion="user">
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
      <Toaster richColors position="bottom-center" mobileOffset={{ bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }} />
      <DemoModeIndicator />
      {showDevtools && (
        <ErrorBoundary fallback={() => null}>
          <Suspense fallback={null}>
            <DevtoolsPanel />
          </Suspense>
        </ErrorBoundary>
      )}
    </MotionConfig>
  )
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
})
